import { Component, type ErrorInfo, type ReactNode } from "react";
import { i18n } from "@njbsoft/i18n";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // No error-reporting backend is wired for the company portal yet, so log
    // the boundary catch to the console for local diagnosis.
    console.error(error, errorInfo.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="p-4 text-center sm:p-8">
          <h1 className="text-2xl font-bold text-on-surface">
            {i18n.t("errors.somethingWentWrong")}
          </h1>
          <p className="mt-2 text-muted">{i18n.t("errors.unexpectedError")}</p>
          <button
            className="mt-4 rounded-md bg-primary px-4 py-2 text-on-primary hover:bg-primary-hover max-sm:min-h-11"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            {i18n.t("errors.tryAgain")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
